<template>
  <RevCardCarousel
    :center="false"
    data-qa="card-carousel"
    :next-alternative-text="i18n(translations.alternativeNext)"
    :prev-alternative-text="i18n(translations.alternativePrevious)"
    :subtitle
    :title
  >
    <div
      v-for="(product, index) in products"
      :key="product.title"
      :ref="setupViewportTrackingProduct(product, tracking)"
      class="relative h-full hover:z-[1] focus-within:z-[1]"
    >
      <ProductCardCarouselCard
        :index
        :is-recommendation
        :product
        :product-granularity
        :tracking
        v-on="addToCartListener ? { addToCart: handleAddToCart(product) } : {}"
        @link-click="emits('link-click', { product })"
      />
      <slot :index name="post-card" :product />
    </div>
    <template v-if="cta" #button>
      <RevButton
        :icon="IconArrowRight"
        :rel="cta?.link?.rel"
        size="medium"
        :target="cta?.link?.target"
        :to="cta?.link?.href"
        variant="primary"
      >
        {{ cta?.label }}
      </RevButton>
    </template>
  </RevCardCarousel>
</template>

<script setup lang="ts">
import { getCurrentInstance } from 'vue'

import type { CTALink } from '@backmarket/http-api/src/api-specs-content/models/cta-link'
import type { ProductGranularity } from '@backmarket/http-api/src/api-specs-content/shared/product-granularity'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCardCarousel } from '@ds/components/CardCarousel'
import { IconArrowRight } from '@ds/icons/IconArrowRight'

import {
  type ExtraTrackingData,
  useImpressionTracking,
} from '../../composables/useProductTracking'
import type { Product } from '../../models/product'

import translations from './ProductCardCarousel.translations'
import ProductCardCarouselCard from './ProductCardCarouselCard.vue'

const i18n = useI18n()

withDefaults(
  defineProps<{
    products: Product[]
    tracking?: ExtraTrackingData
    title?: string
    subtitle?: string
    cta?: CTALink
    productGranularity?: ProductGranularity
    isRecommendation?: boolean
  }>(),
  {
    tracking: () => ({}),
    title: undefined,
    subtitle: undefined,
    cta: undefined,
    productGranularity: undefined,
    isRecommendation: false,
  },
)

const emits = defineEmits<{
  (e: 'add-to-cart', data: { status: string; product: Product }): void
  (e: 'link-click', data: { product: Product }): void
}>()

const addToCartListener = getCurrentInstance()?.vnode.props?.onAddToCart

function handleAddToCart(product: Product) {
  return ({ status }: { status: string }) => {
    emits('add-to-cart', { status, product })
  }
}

const { setupViewportTrackingProduct } = useImpressionTracking()
</script>
